import { SurfCloudAPI } from "../graphql/surfApi.js";
import { surfBedrockChat } from "../services/surfBedrockChat.js";
import { bedrockConverseWithImage } from "../services/bedrockVision.js";
import { getPulsePreset } from "./pulsePresetsCatalog.js";
import { styleFromPreset } from "./presets.js";
import { SURF_STYLE_ANALYZE_SYSTEM, applyStyleExpandOutput, buildStylePreviewDraftPrompt, parseStyleAnalyzeResponse, parseStyleExpandOutput, surfStyleExpandSystem, surfStyleExpandUserPrompt, } from "./stylePrompt.js";
import { cloneSurfStyle } from "./surfStyle.js";
import { createVariationSeed, getAvoidRecentScenarios, pushAvoidRecentScenario, pushAvoidRecentTreatment, } from "./styleVariation.js";
export async function expandSurfStyleFromPrePrompt(cfg, base, prePrompt, realism, intentHint) {
    const variationSeed = createVariationSeed();
    const raw = await surfBedrockChat(cfg, {
        feature: "surf.style.preset.expand",
        systemText: surfStyleExpandSystem(realism),
        userText: surfStyleExpandUserPrompt({
            prePrompt,
            realism,
            variationSeed,
            avoidRecentScenarios: getAvoidRecentScenarios(),
            intentHint,
        }),
        maxTokens: 2048,
    });
    const output = parseStyleExpandOutput(raw);
    pushAvoidRecentTreatment(output.derivedPrompt);
    if (output.subjectMatter)
        pushAvoidRecentScenario(output.subjectMatter);
    return applyStyleExpandOutput(base, output);
}
/** F24 — expand preset Pulse catalog → derivedPrompt + dual palettes. */
export async function expandSurfStylePreset(cfg, presetId, presentationType) {
    const preset = getPulsePreset(presetId);
    if (!preset)
        throw new Error(`Preset stile sconosciuto: ${presetId}`);
    const base = styleFromPreset(presetId, presentationType);
    return expandSurfStyleFromPrePrompt(cfg, base, preset.prePrompt, preset.realism, presentationType);
}
/** @deprecated Use expandSurfStylePreset with catalog id */
export async function expandSurfStyleCategory(cfg, _categoryId, realism, presentationType) {
    const presetId = realism === "photoreal" ? "photo-natural-light-portrait" : "flat-editorial";
    return expandSurfStylePreset(cfg, presetId, presentationType);
}
export async function analyzeSurfStyleReference(cfg, style, imageDataUrl) {
    const base = cloneSurfStyle(style);
    const raw = await bedrockConverseWithImage(cfg, {
        feature: "surf.style.analyze",
        systemText: SURF_STYLE_ANALYZE_SYSTEM,
        userText: "Analyze this reference for presentation background style only.",
        imageDataUrl,
    });
    return parseStyleAnalyzeResponse(raw, base);
}
export async function generateSurfStylePreviewImage(cfg, style, subject) {
    const prompt = buildStylePreviewDraftPrompt(style, subject);
    const job = await SurfCloudAPI.submitAIJob(cfg, {
        kind: "IMAGE_GENERATE",
        provider: "OPENAI",
        feature: "surf.style.preview",
        prompt,
        paramsJson: JSON.stringify({
            size: "1536x1024",
            quality: "medium",
        }),
    });
    const deadline = Date.now() + 120_000;
    const terminal = new Set(["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"]);
    while (Date.now() < deadline) {
        const full = await SurfCloudAPI.getAIJob(cfg, job.jobId);
        if (terminal.has(full.status)) {
            if (full.status !== "COMPLETED") {
                throw new Error(full.errorMessage ?? `Anteprima stile ${full.status}`);
            }
            const key = full.outputAssetKey?.trim();
            if (!key)
                throw new Error("Anteprima stile senza immagine.");
            return { jobId: job.jobId, outputAssetKey: key };
        }
        await new Promise((r) => setTimeout(r, 1500));
    }
    throw new Error("Timeout generazione anteprima stile.");
}
/** Resolve presigned object URL for preview bitmap (png/jpeg). */
export async function resolveSurfPreviewImageUrl(presignedBaseUrl, getIdToken, s3Key) {
    const base = presignedBaseUrl.replace(/\/+$/, "");
    const token = await getIdToken();
    const res = await fetch(`${base}/blob/download-url`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-oidc-token": token,
        },
        body: JSON.stringify({ s3Key }),
    });
    const text = await res.text();
    if (!res.ok)
        throw new Error(`Download URL (${res.status}): ${text.slice(0, 200)}`);
    const json = JSON.parse(text);
    if (!json.url)
        throw new Error("URL anteprima non disponibile.");
    return json.url;
}
